/* src/components/LocationPicker.js */
import React, { useState, useEffect } from 'react';
import cityMap from '../data/cityMap.json'; 

export default function LocationPicker({ 
  country, 
  city, 
  onCountryChange, 
  onCityChange, 
  language, 
  t 
}) {
  const isChinese = language === "zh";
  const [province, setProvince] = useState("");

  // Sync province with city when city changes externally or initially
  useEffect(() => {
    if (country && city && cityMap[country]?.cities[city]) {
      const p = cityMap[country].cities[city].province;
      // Only update if different to avoid loops, though React handles this
      if (p !== province) {
        setProvince(p || "");
      }
    } else if (!city) {
        // If city is cleared, clear province (unless we want to keep it selected)
        // But usually city is cleared when country changes.
        // If country changes, province should be cleared.
    }
  }, [country, city, province]);

  // Helper to extract city keys from the nested structure
  const getProvincesForCountry = (countryName) => {
    if (!cityMap[countryName] || !cityMap[countryName].cities) return [];
    const cities = cityMap[countryName].cities;
    const provinces = new Set();
    Object.values(cities).forEach(city => {
      if (city.province) {
        provinces.add(city.province);
      }
    });
    return Array.from(provinces).sort();
  };

  const getProvinceDisplayName = (countryName, provinceName) => {
    if (!isChinese) return provinceName;
    
    if (!cityMap[countryName] || !cityMap[countryName].cities) return provinceName;
    
    const cities = cityMap[countryName].cities;
    for (const cityKey in cities) {
        if (cities[cityKey].province === provinceName) {
            return cities[cityKey].province_zh || provinceName;
        }
    }
    return provinceName;
  };

  const getCitiesForCountry = (countryName, provinceName) => {
    if (!cityMap[countryName] || !cityMap[countryName].cities) return [];
    const allCities = Object.keys(cityMap[countryName].cities);
    
    const provinces = getProvincesForCountry(countryName);
    if (provinces.length > 0) {
        // If provinces exist, filter by province
        // If no province selected, maybe return empty or all? 
        // In UserForm we returned empty if provinces exist but none selected (except for initial load logic)
        // Let's follow UserForm logic: if provinces exist, we need a province to show cities?
        // Or we can show all if no province selected?
        // UserForm logic:
        // if (provinces.length > 0) { if (!provinceName) return []; ... }
        
        if (!provinceName) {
             // If we have provinces but none selected, we might want to force selection.
             // But for LocationPicker, we might want to be more flexible.
             // However, to be consistent with UserForm:
             return []; 
        }
        return allCities.filter(cityName => 
            cityMap[countryName].cities[cityName].province === provinceName
        ).sort();
    }

    return allCities.sort();
  };

  const getCityDisplayName = (countryName, cityName) => {
    const cityObj = cityMap[countryName]?.cities?.[cityName];
    if (isChinese && cityObj?.city_name_zh) {
      return cityObj.city_name_zh;
    }
    return cityName;
  };

  // We need to handle the case where country changes.
  // The parent calls onCountryChange.
  // We should probably intercept that or let parent handle it.
  // Parent: onCountryChange={(newCountry, firstCity) => { setCountry(newCountry); setCity(firstCity); }}
  
  // If we change country, we want to pick a default city.
  // If the country has provinces, we should pick the first province and its first city?
  // Or just pick the first city available and let the province auto-set?
  // If we pick first city available, the useEffect will set the province.
  
  // But getCitiesForCountry(newCountry) in the original code returned ALL cities.
  // So picking cities[0] worked.
  // Now if we use the new logic, we need to be careful.
  
  // Let's keep the original getCitiesForCountry for the onChange handler to find a default city.
  const getAllCitiesForCountry = (countryName) => {
      if (!cityMap[countryName] || !cityMap[countryName].cities) return [];
      return Object.keys(cityMap[countryName].cities).sort();
  };

  const availableCities = getCitiesForCountry(country, province);
  const provinces = getProvincesForCountry(country);

  return (
    <>
      {/* Country Selection */}
      <div className="form-group">
        <label className="form-label">{t.country}</label>
        <select
          value={country}
          onChange={(e) => {
            const newCountry = e.target.value;
            setProvince(""); // Clear province
            
            // Find a default city
            // We can just pick the first city from the list of all cities
            // And let the useEffect set the province.
            const allCities = getAllCitiesForCountry(newCountry);
            const defaultCity = allCities.length > 0 ? allCities[0] : "";
            
            onCountryChange(newCountry, defaultCity);
          }}
          className="form-select stellar-select"
        >
          <option value="">{isChinese ? "请选择国家" : "Select Country"}</option>
          {Object.keys(cityMap).map((c) => (
            <option key={c} value={c}>
              {isChinese ? cityMap[c].country_name_zh : c}
            </option>
          ))}
        </select>
      </div>

      {/* Province Selection - Enabled if country has provinces */}
      {country && provinces.length > 0 && (
        <div className="form-group">
            <label className="form-label">{isChinese ? "省份/州" : "Province/State"}</label>
            <select
                value={province}
                onChange={(e) => {
                    const newProvince = e.target.value;
                    setProvince(newProvince);
                    // Update city to first city of new province
                    const cities = getCitiesForCountry(country, newProvince);
                    if (cities.length > 0) {
                        onCityChange(cities[0]);
                    } else {
                        onCityChange("");
                    }
                }}
                className="form-select stellar-select"
            >
                <option value="">{isChinese ? "请选择省份/州" : "Select Province/State"}</option>
                {provinces.map(p => (
                    <option key={p} value={p}>{getProvinceDisplayName(country, p)}</option>
                ))}
            </select>
        </div>
      )}

      {/* City Selection - Enabled ONLY if country has cities (and province selected if applicable) */}
      <div className="form-group">
        <label className="form-label">{t.city}</label>
        <select 
          value={city} 
          onChange={(e) => onCityChange(e.target.value)}
          className="form-select stellar-select"
          disabled={!country || (provinces.length > 0 && !province)}
        >
          <option value="">{isChinese ? "请选择城市" : "Select City"}</option>
          {availableCities.map((ct) => (
            <option key={ct} value={ct}>
              {getCityDisplayName(country, ct)}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}